/**
 * The app's transient-feedback bus.
 *
 * Deliberately dependency-free — it imports NOTHING. `api.js` publishes to it from
 * inside the response interceptor, and `api.js` already reads `sessionStorage`
 * directly rather than importing `authService` to avoid a module cycle. A bus that
 * imported React, axios or either service would reintroduce exactly that cycle.
 *
 * Contract:
 *  - Emissions with no host mounted are DROPPED, not queued. A toast fired
 *    immediately before a full-page redirect is therefore never seen; every
 *    current consumer is redirect-free. Making them survive would need a
 *    sessionStorage handoff, which is not worth its failure modes yet.
 *  - Ids come from a monotonic counter, not a timestamp: two emissions inside one
 *    tick would collide on `Date.now()` and React would warn on duplicate keys.
 */

/** @type {Set<(toast: {id: number, type: string, message: string}) => void>} */
const listeners = new Set();

let sequence = 0;

/** Toast severities. `error` and `warning` are announced assertively. */
export const TOAST_TYPES = ['success', 'error', 'warning', 'info'];

/**
 * Publish a transient message.
 *
 * @param {string} message user-facing copy — already localized//humanized
 * @param {'success'|'error'|'warning'|'info'} [type='info']
 */
export const showToast = (message, type = 'info') => {
  const toast = { id: ++sequence, type, message };
  listeners.forEach((listener) => listener(toast));
};

/**
 * Subscribe a host. Returns the unsubscribe function, shaped for direct return
 * from a `useEffect` callback.
 *
 * @param {(toast: {id: number, type: string, message: string}) => void} listener
 * @returns {() => void}
 */
export const subscribeToToasts = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
